import {CookieCategory, getConsentCookieCategories} from "@khan/data-cookies";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

type StorableData =
    | Record<any, any>
    | string
    | boolean
    | number
    | ReadonlyArray<any>;

/**
 * LocalStore is a *super* simple abstraction around localStorage for easy
 * get/set/delete. We may end up wanting something more powerful like
 * BankersBox, but for now this is much lighter weight.
 *
 * If you ever need to completely wipe LocalStore for *all* users when,
 * say, changing the format of data being cached, just bump up the "version"
 * property below.
 */
const LocalStore = {
    // Bump up "version" any time you want to completely wipe LocalStore
    // results. This lets us expire values on all users' LocalStores when
    // deploying a new version, if necessary.
    version: 4,

    keyPrefix: "ka",

    cacheKey: function (key: string): string {
        if (!key) {
            throw new KAError(
                "Attempting to use LocalStore without a key",
                Errors.Internal,
            );
        }

        return [LocalStore.keyPrefix, LocalStore.version, key].join(":");
    },

    /**
     * Get whatever data was associated with key. Returns null if no data
     * is associated with the key, regardless of key's value
     * (null, undefined, "monkey").
     */
    get: function (key: string): any | null {
        if (!LocalStore.isEnabled()) {
            return null;
        }

        try {
            // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
            const data = localStorage[LocalStore.cacheKey(key)];
            if (data) {
                return JSON.parse(data);
            }
        } catch (e: any) {
            // If we had trouble retrieving, like FF's NS_FILE_CORRUPTED
            // http://stackoverflow.com/questions/18877643/error-in-local-storage-ns-error-file-corrupted-firefox
        }

        return null;
    },

    /**
     * Store data associated with key in localStorage
     */
    set: function (
        key: string,
        data: StorableData,
        cookieCategory: CookieCategory,
    ): boolean {
        if (!LocalStore.isEnabled()) {
            return false;
        }

        if (
            // Strictly necessary cookies can always be created
            cookieCategory &&
            cookieCategory !== CookieCategory.StrictlyNecessary &&
            // We also allow cookies in any category to be created in dev
            !RequestInfo.IS_DEV_SERVER
        ) {
            const acceptedCookieGroups = getConsentCookieCategories();
            // if the user has not accepted the cookie category, do not set the cookie
            if (!acceptedCookieGroups.get(cookieCategory)) {
                return false;
            }
        }

        const stringified = JSON.stringify(data);
        const cacheKey = LocalStore.cacheKey(key);

        try {
            // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
            localStorage[cacheKey] = stringified;
        } catch (e: any) {
            // If we had trouble storing in localStorage, we may've run over
            // the browser's 5MB limit. This should be rare, but when hit,
            // clear everything out.
            LocalStore.clearAll();
            return false;
        }

        return true;
    },

    /**
     * Delete whatever data was associated with key
     */
    del: function (key: string): null | undefined {
        if (!LocalStore.isEnabled()) {
            return null;
        }

        const cacheKey = LocalStore.cacheKey(key);
        if (cacheKey in localStorage) {
            // IE throws when deleting a key that's not currently in
            // localStorage.
            // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
            delete localStorage[cacheKey];
        }
    },

    isEnabled: function (): boolean {
        const uid = String(+new Date());
        try {
            sessionStorage[uid] = uid;
            const enabled = sessionStorage[uid] === uid;
            sessionStorage.removeItem(uid);
            return enabled;
        } catch (e: any) {
            return false;
        }
    },

    /**
     * Delete all cached objects from localStorage
     */
    clearAll: function (): null | undefined {
        if (!LocalStore.isEnabled()) {
            return null;
        }

        try {
            let i = 0;
            // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
            while (i < localStorage.length) {
                // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
                const key = localStorage.key(i);
                if (
                    key != null &&
                    key.indexOf(LocalStore.keyPrefix + ":") === 0
                ) {
                    // eslint-disable-next-line no-restricted-syntax -- localStorage access is fine in this utility
                    delete localStorage[key];
                } else {
                    i++;
                }
            }
        } catch (e: any) {
            // If we had trouble accessing .length, like FF's NS_FILE_CORRUPTED
            // http://stackoverflow.com/questions/18877643/error-in-local-storage-ns-error-file-corrupted-firefox
        }
    },
} as const;

if (typeof window !== "undefined") {
    // @ts-expect-error - TS2551 - Property 'LocalStore' does not exist on type 'Window & typeof globalThis'. Did you mean 'localStorage'?
    window.LocalStore = LocalStore;
}
export default LocalStore;
